import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import {
  InternalTransportCostCard,
  InternalTransportCostFilters,
  InternalTransportCostImportBatch,
  InternalTransportCostImportPreview,
  InternalTransportCostImportProcessResult,
  InternalTransportCostImportRow
} from './internal-transport-costs.model';

@Injectable({ providedIn: 'root' })
export class InternalTransportCostsService {
  private readonly baseUrl = `${environment.restApiUrl}internal-transport-costs`;

  constructor(private http: HttpClient) {}

  list(filters: InternalTransportCostFilters): Observable<APIListWrapper<InternalTransportCostCard>> {
    let params = new HttpParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<APIListWrapper<InternalTransportCostCard>>(this.baseUrl, { params });
  }

  get(internalTransportCostCardId: number): Observable<APIResponseWrapper<InternalTransportCostCard>> {
    return this.http.get<APIResponseWrapper<InternalTransportCostCard>>(`${this.baseUrl}/${internalTransportCostCardId}`);
  }

  create(card: InternalTransportCostCard): Observable<APIResponseWrapper<InternalTransportCostCard>> {
    return this.http.post<APIResponseWrapper<InternalTransportCostCard>>(this.baseUrl, card);
  }

  update(internalTransportCostCardId: number, card: InternalTransportCostCard): Observable<APIResponseWrapper<InternalTransportCostCard>> {
    return this.http.put<APIResponseWrapper<InternalTransportCostCard>>(`${this.baseUrl}/${internalTransportCostCardId}`, card);
  }

  template(owningCompanyId?: number, format: 'xlsx' | 'csv' = 'xlsx'): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set('format', format);
    if (owningCompanyId) {
      params = params.set('owningCompanyId', String(owningCompanyId));
    }
    return this.http.get(`${this.baseUrl}/template`, { params, observe: 'response', responseType: 'blob' });
  }

  upload(file: File, owningCompanyId: number): Observable<APIResponseWrapper<InternalTransportCostImportPreview>> {
    const form = new FormData();
    form.append('file', file);
    form.append('owningCompanyId', String(owningCompanyId));
    return this.http.post<APIResponseWrapper<InternalTransportCostImportPreview>>(`${this.baseUrl}/import/batches/upload`, form);
  }

  batches(owningCompanyId?: number): Observable<APIListWrapper<InternalTransportCostImportBatch>> {
    let params = new HttpParams();
    if (owningCompanyId) {
      params = params.set('owningCompanyId', String(owningCompanyId));
    }
    return this.http.get<APIListWrapper<InternalTransportCostImportBatch>>(`${this.baseUrl}/import/batches`, { params });
  }

  batch(batchId: number): Observable<APIResponseWrapper<InternalTransportCostImportPreview>> {
    return this.http.get<APIResponseWrapper<InternalTransportCostImportPreview>>(`${this.baseUrl}/import/batches/${batchId}`);
  }

  updateRow(row: InternalTransportCostImportRow): Observable<APIResponseWrapper<InternalTransportCostImportRow>> {
    return this.http.put<APIResponseWrapper<InternalTransportCostImportRow>>(`${this.baseUrl}/import/rows/${row.rowId}`, {
      values: row.values || {}
    });
  }

  processBatch(batchId: number): Observable<APIResponseWrapper<InternalTransportCostImportProcessResult>> {
    return this.http.post<APIResponseWrapper<InternalTransportCostImportProcessResult>>(`${this.baseUrl}/import/batches/${batchId}/process`, {});
  }

  processRow(rowId: number): Observable<APIResponseWrapper<InternalTransportCostImportRow>> {
    return this.http.post<APIResponseWrapper<InternalTransportCostImportRow>>(`${this.baseUrl}/import/rows/${rowId}/process`, {});
  }
}
